import { Component, OnInit, signal, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { QRCodeComponent } from 'angularx-qrcode';
import { MatDialog } from '@angular/material/dialog';
import {
  ActivatedRoute,
  Params,
} from '@angular/router';

import {
  IWhitelabelAndroidPropertiesConfiguration,
  IWhitelabelIosPropertiesConfiguration,
  WhitelabelConfiguration,
} from '../../models/whitelabel-config';
import {
  qrCodeLinkValidator,
  qrCodeContentValidator,
} from '../../validators/qrcode-link-validator';
import { NiboService } from '../../services/nibo.service';
import { environment } from 'src/environments/environment';
import AzureService from 'src/app/shared/services/azure.service';
import WhitelabelUploadHelper from '../../helpers/upload.helper';
import { guidValidator } from '../../helpers/onesignal-validators';
import { WhitelabelService } from '../../services/whitelabel.service';
import { QrCodeLinkResponse } from '../../interfaces/qrcode.interface';
import { atLeastOneSelected } from '../../validators/form-validators';
import { accentColorValidator, normalizeAccentColor } from '../../validators/accent-color-validator';
import { IOS_CREDENTIALS_DEFAULT_FOLDER } from '../../models/whitelabel-ios-config';
import WhitelabelIosConfigFactory from '../../factories/whitelabel-ios-config-factory';
import WhitelabelAndroidConfigFactory from '../../factories/whitelabel-android-config.factory';
import { AzureBlobUploadPayload } from 'src/app/shared/interfaces/azure/blob-upload.interface';
import { WhitelabelQrcodeViewerComponent } from '../whitelabel-qrcode-viewer/whitelabel-qrcode-viewer.component';
import { WhitelabelAndroidConfig } from '../../models/whitelabel-android-config';

@Component({
  standalone: false,
  selector: 'app-whitelabel-create',
  templateUrl: './whitelabel-create.component.html',
  styleUrls: ['./whitelabel-create.component.css'],
})
export class WhitelabelCreateComponent implements OnInit {
  public createAppForm: FormGroup;

  public readonly savedConfigurations = signal<WhitelabelConfiguration | undefined>(undefined);

  public showDraftModeToggle = true;

  public readonly loading = signal(false);

  private _qrCodeContent = '';

  private controlsNameThatHaveRequiredValidation: string[];

  // Inicializado no construtor, e nao inline: com target ES2022 os inicializadores de
  // campo rodam antes das parameter properties serem atribuidas, e `this.azureService`
  // ainda seria undefined aqui.
  private whitelabelUploadHelper: WhitelabelUploadHelper;

  private readonly qrCodeImageReference = viewChild.required<QRCodeComponent>('qrCodeImage');

  constructor(
    private modalService: MatDialog,
    private formBuilder: FormBuilder,
    private niboService: NiboService,
    private azureService: AzureService,
    private toastrService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private whitelabelService: WhitelabelService,
  ) {
    this.whitelabelUploadHelper = new WhitelabelUploadHelper(this.azureService);

    this.createAppForm = this.formBuilder.group(
      {
        accountantId: new FormControl(null, [
          Validators.required,
          Validators.maxLength(36),
          Validators.minLength(36),
          this.lowerCaseValidator(),
        ]),
        // No blur, para que o `#` seja prefixado uma vez sobre o valor inteiro em vez de a
        // cada tecla — normalizar durante a digitação joga o cursor para o fim do campo.
        color: new FormControl(null, {
          validators: [accentColorValidator],
          updateOn: 'blur',
        }),
        intentUrl: new FormControl(null),
        intentScheme: new FormControl(null),
        draftMode: new FormControl(false, Validators.required),
        iosSelect: new FormControl(null),
        androidSelect: new FormControl(null),
        versionCode: new FormControl(null),
        versionName: new FormControl(null),
        name: new FormControl(null, Validators.required),
        deepLink: new FormControl('link.nibo.com.br', [Validators.required]),
        qrCodeContent: new FormControl(null, {
          validators: [Validators.required, qrCodeContentValidator],
          updateOn: 'blur',
        }),
        deepLinkQrCodeLink: new FormControl(null, [
          Validators.required,
          qrCodeLinkValidator,
        ]),
        oneSignalAppId: new FormControl(null, [
          Validators.required,
          guidValidator,
        ]),
        oneSignalApiKey: new FormControl(null, [Validators.required]),
      },
      {
        validators: atLeastOneSelected('iosSelect', 'androidSelect'),
      }
    );

    this.controlsNameThatHaveRequiredValidation = Object.keys(
      this.createAppForm.controls
    ).filter((control) =>
      this.createAppForm.controls[control].getError('required')
    );

    this.handleDraftControl();

    this.createAppForm.controls['deepLink'].disable();
    this.createAppForm.controls['intentUrl'].disable();
    this.createAppForm.controls['intentScheme'].disable();

    this.createAppForm.controls['color'].valueChanges.subscribe({
      next: (value) => {
        const normalized = normalizeAccentColor(value);

        if (normalized === value) return;

        this.createAppForm.controls['color'].setValue(normalized, {
          emitEvent: false,
        });
      },
    });

    this.createAppForm.controls['oneSignalApiKey'].valueChanges.subscribe({
      next: (value) => {
        try {
          const decodedValue = atob(value);
          this.createAppForm.controls['oneSignalApiKey'].setValue(
            decodedValue,
            { emitEvent: false }
          );
        } catch { }
      },
    });
  }

  private lowerCaseValidator(): (control: FormControl) => null {
    return (control: FormControl) => {
      const value = control.value;
      if (value && typeof value === 'string') {
        const lowerCaseValue = value.toLowerCase();
        if (value !== lowerCaseValue) {
          control.setValue(lowerCaseValue);
        }
      }
      return null;
    };
  }

  private handleAlreadySavedAccountantConfigurations(
    accountantId: string
  ): void {
    this.loading.set(true);

    this.whitelabelService.getConfiguration(accountantId).subscribe({
      next: (accountantConfiguration) => {
        this.savedConfigurations.set(accountantConfiguration);
        this.updateFormWithSavedValues(accountantConfiguration);

        this.loading.set(false);
      },
    });
  }

  private hasAccountantIdInRouteParams(params: Params): boolean {
    return !!params['accountantId'];
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe({
      next: (params) => {
        if (this.hasAccountantIdInRouteParams(params)) {
          const accountantId = params['accountantId'] as string;
          this.handleAlreadySavedAccountantConfigurations(accountantId);
        }
      },
    });
  }

  public onFileUploadError() {
    this.toastrService.error('Arquivo inválido');
  }

  public onSubmit() {
    if (!this.createAppForm.valid) {
      this.createAppForm.markAllAsTouched();
      return;
    }

    const whitelabelConfig = new WhitelabelConfiguration({
      accountantId: this.accountantId,
      intentUrl: this.intentUrl,
      isDraft: this.isDraft,
      intentScheme: this.intentScheme,
      versions: {
        versionCode: this.versionCode,
        versionName: this.versionName,
      },
      styles: {
        accentColor: this.color,
      },
      deepLink: {
        url: this.deepLink,
        qrCode: this.qrCodeLink,
        host: this.deepLink,
        qrCodeContent: this.qrCodeContent,
      },
      oneSignal: {
        apiKey: this.oneSignalApiKey,
        appId: this.oneSignalAppId,
      },
      name: this.name,
      android: null,
      ios: null,
    });

    if (this.androidSelected) {
      const androidConfigFactory = new WhitelabelAndroidConfigFactory()
        .withName(this.name)
        .withIntentScheme(this.intentScheme)
        .withPackageName(this.packageName)
        .withStatus(this.androidStatus)
        .withSha256CertificateFingerPrint(this.certificateSHA256)

        .withKeytool(this.keytoolLink)
        .withGoogleServiceJson(this.googleServiceJsonLink)
        .withIcons(this.androidIconsLinks)
        .withAppIcon(this.androidAppIconLink)
        .withStore(this.storeLink)
        .withServiceAccountEmail(this.serviceAccountEmail)
        .withServiceAccountPrivateKey(this.serviceAccountPrivateKey);

      const androidConfig = androidConfigFactory.build();

      const envPropertiesFile = androidConfig.generateEnvPropertiesFile(this.deepLink);
      this.whitelabelUploadHelper
        .uploadSingle(envPropertiesFile, this.accountantId)
        .subscribe();
        
      const androidProperties: IWhitelabelAndroidPropertiesConfiguration = {
        status: androidConfig.status,
        androidPackageName: androidConfig.packageName,
        sha256CertificateFingerPrint: androidConfig.sha256CertificateFingerPrint,
        serviceAccountEmail: androidConfig.serviceAccountEmail,
        serviceAccountPrivateKey: androidConfig.serviceAccountPrivateKey,
        files: {
          appIcon: androidConfig.appIconLink,
          googleServiceJson: androidConfig.googleServicesJsonLink,
          icons: androidConfig.iconsLinks,
          keytool: androidConfig.keytoolLink,
          store: androidConfig.storeLink,
        },
      };

      whitelabelConfig.android = androidProperties;
    }

    if (this.iosSelected) {
      const iosConfigFactory = new WhitelabelIosConfigFactory()
        .withName(this.name)
        .withAppId(this.appId)
        .withIntentScheme(this.intentScheme)
        .withStatus(this.iosStatus)
        .withAssociatedDomain(this.deepLink)
        .withDevelopmentTeam(this.developmentTeam)
        .withIosBundleId(this.iosBundleId)
        .withApplicationGroup(this.applicationGroup)
        .withApiKeyId(this.apiKeyId)
        .withApiKeyIssuerId(this.apiKeyIssuerId)
        .withApiTokenBase64(this.apiTokenBase64)
        .withOneSignalBundleId(this.oneSignalBundleId)
        .withAppProvisionProfielSpecifier(this.appProvisionProfileSpecifier)
        .withOnesignalProvisionProfielSpecifier(
          this.onesignalProvisionProfileSpecifier
        )

        .withGoogleServicePlist(this.googleServicePlistLink)
        .withAppProvisionProfile(this.appProvisionProfileLink)
        .withOneSignalProvisionProfile(this.oneSignalProvisionProfileLink)
        .withIcons(this.iosIconsLinks)
        .withAppIcon(this.appIconLink)
        .withP12Certificate(this.p12CertificateLink)
        .withDataExpiritionCertificate(this.iosCertificateExpiryDate);

      const iosConfig = iosConfigFactory.build();

      const appXcconfigFile = iosConfig.generateAppReleaseXcconfigFile(this.deepLink);
      const appXCConfigPath = `${this.accountantId}/${IOS_CREDENTIALS_DEFAULT_FOLDER}`;

      this.whitelabelUploadHelper
        .uploadSingle(appXcconfigFile, appXCConfigPath)
        .subscribe();

      const oneSignalXcconfigFile = iosConfig.generateOneSignalReleaseXcconfigFile();
      const oneSignalXCConfigPath = `${this.accountantId}/${IOS_CREDENTIALS_DEFAULT_FOLDER}`;

      this.whitelabelUploadHelper
        .uploadSingle(oneSignalXcconfigFile, oneSignalXCConfigPath)
        .subscribe();


      const iosProperties: IWhitelabelIosPropertiesConfiguration = {
        status: iosConfig.status,
        apiKeyId: iosConfig.apiKeyId,
        apiKeyIssuerId: iosConfig.apiKeyIssuerId,
        apiTokenBase64: iosConfig.apiTokenBase64,
        appId: iosConfig.appId,
        applicationGroup: iosConfig.applicationGroup,
        developmentTeam: iosConfig.developmentTeam,
        iosBundleId: iosConfig.iosBundleId,
        iosCertificateExpiryDate: iosConfig.iosCertificateExpiryDate,
        oneSignal: {
          bundleId: iosConfig.oneSignalBundleId,
          provisionProfileSpecifier:
            iosConfig.oneSignalProvisionProfileSpecifier,
        },
        provisionProfileSpecifier: iosConfig.appProvisionProfileSpecifier,
        files: {
          appIcon: iosConfig.appIconLink,
          appProvisionProfile: iosConfig.appProvisionProfileLink,
          certificate: iosConfig.certificateLink,
          googleServicePlist: iosConfig.googleServicePlistLink,
          oneSignalProvisionProfile: iosConfig.oneSignalProvisionProfileLink,
          icons: iosConfig.iconsLink,
        },
      };

      whitelabelConfig.ios = iosProperties;
    }

    if (this.intentScheme !== this.savedConfigurations()?.intentScheme) {
      const url = WhitelabelConfiguration.toIntentUrl(this.intentScheme);

      this.niboService.getGoNiboUrl(url).subscribe({
        next: (intentUrl: string) => {
          whitelabelConfig.intentUrl = intentUrl;
          this.saveConfiguration(whitelabelConfig);
        },
        error: () => {
          this.toastrService.error('Erro ao gerar url de intent');
        },
      });

      return;
    }

    this.saveConfiguration(whitelabelConfig);
  }

  private saveConfiguration(whitelabelConfig: WhitelabelConfiguration): void {
    this.whitelabelService.saveConfigurations(whitelabelConfig).subscribe({
      next: () => {
        this.toastrService.success('Configuração salva com sucesso');
        if (!this.isDraft) this.showDraftModeToggle = false;
      },
      error: () => {
        this.toastrService.error('Erro ao salvar configurações');
      },
    });
  }

  public openQrCodeModal() {
    this.modalService.open(WhitelabelQrcodeViewerComponent, {
      data: {
        link: this.qrCodeContent,
      },
    });
  }

  public onFetchQrCodeLink(qrCodeResponse: QrCodeLinkResponse) {
    this.createAppForm.controls['qrCodeContent'].patchValue(
      qrCodeResponse.shorterUrl
    );
  }

  public onQrCodeLoad() {
    if (this._qrCodeContent !== this.qrCodeContent) this.saveQrCode();
  }

  public get qrCodeContent() {
    return this.createAppForm.controls['qrCodeContent'].value as string;
  }

  public get qrCodeLink() {
    return this.createAppForm.controls['deepLinkQrCodeLink'].value as string;
  }

  public get accountantId() {
    return this.createAppForm.controls['accountantId'].value;
  }

  private get controlsToRemoveRequiredValidation(): string[] {
    const controlsToChange: string[] = Object.keys(
      this.createAppForm.controls
    ).filter((control) => !['accountantId', 'name'].includes(control));
    return controlsToChange;
  }

  private handleDraftControl(): void {
    if (this.createAppForm.controls['draftMode']) {
      this.createAppForm.controls['draftMode'].valueChanges.subscribe({
        next: (value) => {
          if (value) {
            this.controlsToRemoveRequiredValidation.forEach((control) => {
              this.createAppForm.controls[control].removeValidators(
                Validators.required
              );
              this.createAppForm.controls[control].updateValueAndValidity({
                emitEvent: false,
              });
            });
          } else {
            this.controlsNameThatHaveRequiredValidation.forEach((control) => {
              this.createAppForm.controls[control].addValidators(
                Validators.required
              );
              this.createAppForm.controls[control].updateValueAndValidity({
                emitEvent: false,
              });
            });
          }
        },
      });
    }
  }

  private get isDraft(): boolean {
    return this.createAppForm.controls['draftMode'].value;
  }

  private get intentScheme(): string {
    return this.createAppForm.controls['intentScheme'].value;
  }

  public get intentUrl(): string {
    return this.createAppForm.controls['intentUrl'].value;
  }

  private get iosForm(): FormGroup {
    return this.createAppForm.controls['iosForm'] as FormGroup;
  }

  private get androidForm(): FormGroup {
    return this.createAppForm.controls['androidForm'] as FormGroup;
  }

  private get androidSelected(): boolean {
    return this.createAppForm.controls['androidSelect'].value;
  }

  private get iosSelected(): boolean {
    return this.createAppForm.controls['iosSelect'].value;
  }

  private get versionName() {
    return this.createAppForm.controls['versionName'].value;
  }

  private get versionCode() {
    return this.createAppForm.controls['versionCode'].value;
  }

  private get color() {
    return this.createAppForm.controls['color'].value;
  }

  private get oneSignalApiKey() {
    return this.createAppForm.controls['oneSignalApiKey'].value;
  }

  private get oneSignalAppId() {
    return this.createAppForm.controls['oneSignalAppId'].value;
  }

  public get deepLink() {
    return this.createAppForm.controls['deepLink'].value as string;
  }

  private get name() {
    return this.createAppForm.controls['name'].value;
  }

  //android

  private get serviceAccountPrivateKey(): string | null {
    return this.androidForm.controls['serviceAccountPrivateKey']?.value;
  }

  private get serviceAccountEmail(): string | null {
    return this.androidForm.controls['serviceAccountEmail']?.value;
  }

  private get certificateSHA256(): string | null {
    return this.androidForm.controls['sha256CertificateFingerPrint']?.value || null;
  }

  private get packageName(): string | null {
    return this.androidForm.controls['packageName']?.value || null;
  }

  private get androidIconsLinks(): string | null {
    return this.androidForm.controls['iconsLinks']?.value || null;
  }

  private get androidStatus(): number {
    return this.androidForm.controls['status'].value;
  }

  private get keytoolLink(): string | null {
    return this.androidForm.controls['keytoolLink']?.value || null;
  }

  private get storeLink(): string | null {
    return this.androidForm.controls['storeLink']?.value || null;
  }

  private get googleServiceJsonLink(): string | null {
    return this.androidForm.controls['googleServicesJsonLink']?.value || null;
  }

  private get androidAppIconLink(): string | null {
    return this.androidForm.controls['appIconLink']?.value || null;
  }

  //ios

  private get appProvisionProfileSpecifier() {
    return this.iosForm.controls['appProvisionProfileSpecifier']?.value;
  }

  private get onesignalProvisionProfileSpecifier(): string | null {
    return this.iosForm.controls['onesignalProvisionProfileSpecifier']?.value;
  }

  private get iosStatus(): number {
    return this.iosForm.controls['status'].value;
  }

  private get oneSignalBundleId(): string | null {
    return this.iosForm.controls['oneSignalBundleId'].value;
  }

  private get apiKeyId(): string | null {
    return this.iosForm.controls['apiKeyId'].value;
  }

  private get appId(): string | null {
    return this.iosForm.controls['appId'].value;
  }

  private get apiKeyIssuerId(): string | null {
    return this.iosForm.controls['apiKeyIssuerId'].value;
  }

  private get apiTokenBase64(): string | null {
    return this.iosForm.controls['apiTokenBase64'].value;
  }

  private get applicationGroup(): string | null {
    return this.iosForm.controls['applicationGroup'].value;
  }

  private get iosBundleId(): string | null {
    return this.iosForm.controls['iosBundleId'].value;
  }

  private get developmentTeam(): string | null {
    return this.iosForm.controls['developmentTeam'].value;
  }

  private get googleServicePlistLink(): string | null {
    return this.iosForm.controls['googleServicePlistLink'].value;
  }

  public get appIconLink(): string | null {
    return this.iosForm.controls['appIconLink']?.value;
  }

  private get appProvisionProfileLink(): string | null {
    return this.iosForm.controls['appProvisionProfileLink']?.value;
  }

  private get oneSignalProvisionProfileLink(): string | null {
    return this.iosForm.controls['oneSignalProvisionProfileLink']?.value;
  }

  private get p12CertificateLink(): string | null {
    return this.iosForm.controls['p12CertificateLink']?.value;
  }

  private get iosIconsLinks(): string | null {
    return this.iosForm.controls['iconsLink']?.value || null;
  }

  private get iosCertificateExpiryDate(): string | null {
    return this.iosForm.controls['iosCertificateExpiryDate']?.value || null;
  }

  public saveQrCode() {
    const qrCodeDatUrl = this.convertQrCodeComponentToDataUrl();
    const qrCodeBlob = this.convertQrCodeToBlob(qrCodeDatUrl);

    const qrCodeFile = new File([qrCodeBlob], qrCodeBlob.text.name, {
      type: qrCodeBlob.type,
    });

    const uploadData: AzureBlobUploadPayload = {
      blobName: `QRCode_${this.accountantId}.jpg`,
      container: `${environment.azure.whitelabel.qrCodesContainer}`,
      file: qrCodeFile,
      storageEndpoint: `${environment.azure.whitelabel.storageEndpoint}`,
    };

    this.azureService.uploadBlob(uploadData).subscribe({
      next: (data: { fileLink: string }) => {
        this.toastrService.info('QR Code enviado com sucesso');
        this.createAppForm.controls['deepLinkQrCodeLink'].patchValue(
          data.fileLink
        );
      },
    });
  }

  private convertQrCodeComponentToDataUrl() {
    const qrCodeDataUrl = this.qrCodeImageReference().qrcElement.nativeElement
      .querySelector('canvas')
      .toDataURL('image/png');

    return qrCodeDataUrl;
  }

  private convertQrCodeToBlob(base64Image: string) {
    const parts = base64Image.split(';base64,');
    const imageType = parts[0].split(':')[1];
    const decodedData = window.atob(parts[1]);
    const uInt8Array = new Uint8Array(decodedData.length);

    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: imageType });
  }

  private updateFormWithSavedValues(appConfig: WhitelabelConfiguration) {

    this.createAppForm.controls['accountantId'].setValue(
      appConfig.accountantId
    );

    this.createAppForm.controls['iosSelect'].setValue(appConfig.hasIos());
    this.createAppForm.controls['androidSelect'].setValue(
      appConfig.hasAndroid()
    );

    this.createAppForm.controls['draftMode'].setValue(
      appConfig.isDraft || false
    );

    this.createAppForm.controls['name'].setValue(appConfig.name);

    if (appConfig.deepLink) {
      this.createAppForm.controls['deepLink'].setValue(`${appConfig.accountantId}.deeplink.nibo.com.br`);
      this.createAppForm.controls['deepLinkQrCodeLink'].setValue(
        appConfig.deepLink.qrCode
      );

      this._qrCodeContent = appConfig.deepLink.qrCodeContent;
      this.createAppForm.controls['qrCodeContent'].setValue(
        appConfig.deepLink.qrCodeContent
      );
    }

    if (appConfig.oneSignal) {
      this.createAppForm.controls['oneSignalApiKey'].setValue(
        appConfig.oneSignal.apiKey
      );
      this.createAppForm.controls['oneSignalAppId'].setValue(
        appConfig.oneSignal.appId
      );
    }

    this.createAppForm.controls['color'].setValue(
      appConfig.styles?.accentColor
    );

    this.createAppForm.controls['versionCode'].setValue(
      appConfig.versions?.versionCode || null
    );
    this.createAppForm.controls['versionName'].setValue(
      appConfig.versions?.versionName || null
    );

    this.createAppForm.controls['intentUrl'].setValue(
      appConfig.intentUrl || null
    );

    this.createAppForm.controls['intentScheme'].setValue(appConfig.intentScheme || null);
  }

  public onAndroidUpdate(data: WhitelabelAndroidConfig): void {
    const intentScheme = WhitelabelConfiguration.toIntentScheme(data.packageName);
    this.createAppForm.controls['intentScheme'].setValue(intentScheme);
  }
}
